<template>
	<div v-show="isEditing" ref="editContainer" class="editor-container" id="editor-container">
		<img
			:src="currentImageUrl"
			ref="annotationImage"
			id="annotation-image"
			crossorigin="anonymous"
			style="width: 100vw; height: 100vh; object-fit: cover" />
		<v-progress-circular
			v-show="isSaving"
			color="primary"
			:size="72"
			:width="5"
			indeterminate
			class="image-progress"></v-progress-circular>

		<v-btn
			id="close-editing"
			v-show="!showControls && !cropping"
			color="black"
			rounded
			icon
			class="close-editing mb-4 fixed mt-4 mr-4"
			@click="discard">
			<v-icon>icon-close-large</v-icon>
		</v-btn>
		<v-btn
			id="cancel-crop"
			v-show="cropping"
			color="editor-menu"
			rounded
			class="close-editing cancel-crop mb-4 fixed mt-4 mr-4"
			@click="reset(false)">
			Cancel Crop
		</v-btn>
		<div ref="cropContainer" v-show="cropping" id="crop-container"></div>

		<div
			id="editor-menu"
			class="fixed d-flex"
			style="top: 0; left: 0; height: 100%; z-index: 100"
			:style="{ display: isEditing ? 'unset' : 'none !important' }">
			<div
				class="fixed d-flex editor-menu ml-4 mt-4"
				style="width: 64px; z-index: 1"
				:class="{ 'flex-row': isMobileLandscape, 'flex-column': !isMobileLandscape }">
				<v-btn id="toggle-edit" color="editor-menu" icon class="mb-4 close-menu" @click="toggleEdit">
					<v-icon>
						{{ showControls ? "icon-close-large" : "icon-pencil-edit-stroke" }}
					</v-icon>
				</v-btn>

				<div
					:class="{
						'd-flex': showControls,
						'flex-row': isMobileLandscape,
						'flex-column': !isMobileLandscape,
						'ml-4': isMobileLandscape,
					}"
					v-show="showControls">
					<v-btn
						id="undo"
						color="editor-menu"
						elevation="3"
						rounded
						icon
						@click="undo"
						class="undo"
						:disabled="!canUndo"
						:class="{
							'rounded-t-circle': !isMobileLandscape,
							'rounded-ts-circle': isMobileLandscape,
							'rounded-bs-circle': isMobileLandscape,
							disabled: !canUndo,
						}">
						<v-icon>icon-arrow-undo-up-left</v-icon>
					</v-btn>
					<v-btn color="editor-menu" elevation="3" rounded icon>
						<ColorPicker :value="annotationColor" @input="updateColor" source="annotation"></ColorPicker>
					</v-btn>
					<v-btn
						id="dot-marker"
						color="editor-menu"
						elevation="3"
						icon
						@click="dot"
						class="border-primary dot-marker"
						:class="{
							'border-top': !isMobileLandscape,
							'border-side': isMobileLandscape,
							selected: isMarkerSelected(DotMarker.typeName),
						}">
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
							<path :fill="annotationColor" d="M17 12a5 5 0 1 1-10 0 5 5 0 0 1 10 0Z" />
							<path
								:fill="annotationColor"
								d="M23 12c0-6.075-4.925-11-11-11S1 5.925 1 12s4.925 11 11 11v1C5.373 24 0 18.627 0 12S5.373 0 12 0s12 5.373 12 12-5.373 12-12 12v-1c6.075 0 11-4.925 11-11Zm-7 0a4 4 0 1 0-8 0 4 4 0 0 0 8 0Zm2 0a6 6 0 1 1-12 0 6 6 0 0 1 12 0Z" />
						</svg>
					</v-btn>
					<v-btn
						id="freehand-marker"
						color="editor-menu"
						elevation="3"
						icon
						@click="freehand"
						:class="{ selected: isMarkerSelected(FreehandMarker.typeName) }">
						<svg width="24" height="20" viewBox="0 0 24 20" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M22 3.46583C19.4953 3.25192 15.4982 2.07439 13.085 2.66322C12.1037 2.90263 10.7861 5.79369 11.6081 6.35521C12.6433 7.06248 13.2961 8.11937 14.7498 8.74011C16.9491 9.6792 17.5556 10.3017 18.5092 12.134C20.4916 15.9431 15.7388 17.0872 12.5748 17.0872C9.26797 17.0872 -0.640622 17.5 2.66617 17.5"
								:stroke="annotationColor"
								stroke-width="4"
								stroke-linecap="round" />
						</svg>
					</v-btn>
					<v-btn
						id="line-marker"
						color="editor-menu"
						elevation="3"
						icon
						@click="lineMarker"
						:class="{ selected: isMarkerSelected(LineMarker.typeName) }">
						<svg width="20" height="36" viewBox="0 0 20 36" fill="none" xmlns="http://www.w3.org/2000/svg">
							<line
								x1="18.4549"
								y1="28.1453"
								x2="1.35642"
								y2="9.90687"
								:stroke="annotationColor"
								stroke-width="3" />
						</svg>
					</v-btn>
					<v-btn
						id="arrow-marker"
						color="editor-menu"
						elevation="3"
						icon
						@click="arrowMarker"
						:class="{ selected: isMarkerSelected(ArrowMarker.typeName) }">
						<v-icon>icon-arrow-top-left-large</v-icon>
					</v-btn>
					<v-btn
						id="text-marker"
						color="editor-menu"
						elevation="3"
						icon
						@click="textMarker"
						:class="{ selected: isMarkerSelected(TextMarker.typeName) }">
						<svg width="18" height="21" viewBox="0 0 18 21" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M17.9636 7.4L16.9636 7.775L13.5636 2.775H10.8136V18.925L13.4886 19.75L12.8636 21H4.76357V19.975L7.11357 18.125V2.775H4.43857L1.03857 7.775L0.0385743 7.4V0.899999L1.56357 1.35H16.4386L17.9636 0.899999V7.4Z"
								:fill="annotationColor" />
						</svg>
					</v-btn>
					<v-menu :nudge-right="44" :attach="true">
						<template v-slot:activator="{ on, attrs }">
							<v-btn
								id="shape-menu"
								color="editor-menu"
								elevation="3"
								icon
								v-bind="attrs"
								v-on="on"
								:class="{
									selected:
										isMarkerSelected(FrameMarker.typeName) ||
										isMarkerSelected(EllipseMarker.typeName),
								}">
								<svg
									width="24"
									height="24"
									viewBox="0 0 24 24"
									fill="none"
									xmlns="http://www.w3.org/2000/svg">
									<path
										d="M21 16C21 13.2386 18.7614 11 16 11C13.2386 11 11 13.2386 11 16C11 18.7614 13.2386 21 16 21V24C11.5817 24 8 20.4183 8 16C8 11.5817 11.5817 8 16 8C20.4183 8 24 11.5817 24 16C24 20.4183 20.4183 24 16 24V21C18.7614 21 21 18.7614 21 16Z"
										:fill="annotationColor" />
									<path
										d="M16 0V10L12 12L9 16H0V0H16ZM3 13H9.5L10.5 10.5L11.5 9.5L13 8.66927V3H3V13Z"
										:fill="annotationColor" />
								</svg>
							</v-btn>
						</template>
						<v-list>
							<v-list-item class="pa-0">
								<v-btn
									id="rectangle-marker"
									color="editor-menu"
									icon
									@click="rectangleMarker"
									:class="{ selected: isMarkerSelected(FrameMarker.typeName) }">
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg">
										<rect
											x="1.5"
											y="1.5"
											width="13"
											height="13"
											:stroke="annotationColor"
											stroke-width="3" />
									</svg>
								</v-btn>
							</v-list-item>
							<v-list-item class="pa-0">
								<v-btn
									id="ellipse-marker"
									color="editor-menu"
									icon
									@click="ellipseMarker"
									:class="{ selected: isMarkerSelected(EllipseMarker.typeName) }">
									<svg
										width="16"
										height="16"
										viewBox="0 0 16 16"
										fill="none"
										xmlns="http://www.w3.org/2000/svg">
										<circle
											cx="8"
											cy="8"
											r="6.5"
											fill="#F3F5F9"
											:stroke="annotationColor"
											stroke-width="3" />
									</svg>
								</v-btn>
							</v-list-item>
						</v-list>
					</v-menu>

					<v-btn
						id="crop"
						color="editor-menu"
						elevation="3"
						icon
						@click="initializeCropper(true)"
						class="crop border-primary"
						:class="{ 'border-top': !isMobileLandscape, 'border-side': isMobileLandscape }">
						<v-icon>icon-crop</v-icon>
					</v-btn>

					<v-btn
						id="remove-marker"
						color="editor-menu"
						elevation="3"
						icon
						@click="deleteMarker"
						class="trash"
						:disabled="!currentSelectedMarker"
						:class="{
							'rounded-b-circle': !isMobileLandscape,
							'rounded-te-circle': isMobileLandscape,
							'rounded-be-circle': isMobileLandscape,
							disabled: !currentSelectedMarker,
						}">
						<v-icon>icon-trash-full-stroke</v-icon>
					</v-btn>
				</div>
			</div>

			<div id="marker-container" ref="container"></div>
		</div>
		<div class="d-flex justify-center pa-4 absolute bottom-container">
			<v-btn id="reset-image" color="primaryDark" rounded size="large" variant="flat" @click="reset">
				{{ cropping ? "Reset" : "Cancel" }}
			</v-btn>
			<v-btn id="save-image" color="primary" rounded size="large" variant="flat" @click="save" class="ml-2">
				{{ cropping ? "Apply" : "Save" }}
			</v-btn>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { ref, onMounted, watch, computed, onUnmounted, reactive, getCurrentInstance } from "vue";
	import Cropper, { CropperImage } from "cropperjs";
	import type { Selection } from "@cropper/element-selection";
	import * as Sentry from "@sentry/vue";

	import {
		type AnnotationState,
		ArrowMarker,
		FreehandMarker,
		LineMarker,
		TextMarker,
		FrameMarker,
		EllipseMarker,
		MarkerArea,
		Renderer,
		TextMarkerEditor,
		MarkerEditorEventData,
		FontSize,
		MarkerBase,
		LinearMarkerEditor,
		MarkerBaseEditor,
		FreehandMarkerEditor,
		ShapeMarkerEditor,
		IPoint,
		ISize,
		LinearMarkerBase,
	} from "@markerjs/markerjs3";

	import DotMarker from "./markers/DotMarker";
	import LSTextMarker from "./markers/LSTextMarker";
	import DotMarkerEditor from "./markers/DotMarkerEditor";
	import LSTextMarkerEditor from "./markers/LSTextMarkerEditor";
	import ColorPicker from "@/components/tags/ColorPicker.vue";
	import DeviceUtility from "@/helpers/device";
	import { MediaType } from "@/enums/MediaType";
	import { nextTick } from "vue";

	const instance = getCurrentInstance();

	const props = defineProps<{
		targetImage: string | undefined;
		isEditing: boolean;
		conversationId: string;
		snapshotId: string | undefined;
	}>();

	const emits = defineEmits<{
		(event: "imageSaved", image: Blob, clearEditor: boolean): Promise<void>;
		(event: "discard", force: boolean): void;
	}>();

	const annotationImage = ref<HTMLImageElement>();
	const editContainer = ref<HTMLDivElement>();
	const container = ref<HTMLDivElement | null>(null);
	const editor = ref<MarkerArea | null>(null);
	const currentEditorTypeName = ref<string | null>();
	const showControls = ref<boolean>(true);
	const wasShowingControls = ref<boolean>(false);
	const annotationColor = ref<string>("#000");
	const orientation = ref<string>(screen.orientation?.type ?? DeviceUtility.fallbackOrientation);
	const cropping = ref<boolean>(false);
	const cropper = ref<Cropper>();
	const cropContainer = ref<HTMLDivElement>();
	const mobile = ref(DeviceUtility.isMobile);
	const currentSelectedMarker = ref<MarkerBaseEditor | undefined>();
	const currentState = ref<AnnotationState | null>();
	const isSaving = ref<boolean>(false);
	const currentImageUrl = ref<string | undefined>();
	const textWidthContext = ref<CanvasRenderingContext2D>();
	const undoEnabled = ref<boolean>(false);
	const lastTextMarker = ref<TextMarker>();
	const imageQuality = ref<number>(1.0);
	const helperCanvas = ref<HTMLCanvasElement>();

	defineExpose({
		discard: discard,
		editor,
	});

	onMounted(async () => {
		if (screen.orientation) {
			screen.orientation.addEventListener("change", onOrientationChange);
		} else {
			window.addEventListener("resize", onWindowResize);
		}

		document.addEventListener("keydown", onKeyDown);

		helperCanvas.value = document.createElement("canvas");

		let imageUrl: string | undefined = props.targetImage;

		console.log("imageUrl", imageUrl);
		if (!imageUrl?.startsWith("data:image")) {
			imageUrl = await imageToDataUrl(props.targetImage);
		}

		currentImageUrl.value = imageUrl;

		if (annotationImage.value) {
			annotationImage.value.onload = async () => {
				if (annotationImage.value) {
					clear(true);
					initializeEditor();
					setDimensions();
				}
			};
		}

		const canvas = document.createElement("canvas");
		const context = canvas.getContext("2d");

		if (context) {
			textWidthContext.value = context;
		}
	});

	onUnmounted(() => {
		if (screen.orientation) {
			screen.orientation.removeEventListener("change", onOrientationChange);
		} else {
			window.removeEventListener("resize", onWindowResize);
		}

		document.removeEventListener("keydown", onKeyDown);
	});

	const isMobileLandscape = computed(() => {
		const landscape = orientation.value.includes("landscape");
		return mobile.value && landscape;
	});

	const canUndo = computed(() => {
		return undoEnabled.value;
	});

	function onOrientationChange(event: Event) {
		orientation.value = screen.orientation.type;
		requestAnimationFrame(() => {
			// Goes against out dev standards but iOS needs this short time for things to settle
			// during an orientation change. Otherwise, the image dimensions are calculated incorrectly
			// because the screen dimensions that come back are still from the previous orientation

			// TODO: Move to window resize instead of orientation change?
			setTimeout(async () => {
				await updateTargetImage(currentImageUrl.value, false);
				setDimensions();
			}, 200);
		});
	}

	function onWindowResize(event: Event) {
		const fallbackOrientation = DeviceUtility.fallbackOrientation;
		orientation.value = fallbackOrientation;

		requestAnimationFrame(() => {
			// Goes against out dev standards but iOS needs this short time for things to settle
			// during an orientation change. Otherwise, the image dimensions are calculated incorrectly
			// because the screen dimensions that come back are still from the previous orientation
			setTimeout(async () => {
				await updateTargetImage(currentImageUrl.value, false);
				setDimensions();
			}, 200);
		});
	}

	function onKeyDown(evt: KeyboardEvent) {
		if (evt.key === "Delete" && currentSelectedMarker.value && editor.value) {
			editor.value.deleteSelectedMarkers();
		}
	}

	function deleteMarker() {
		if (editor.value && currentSelectedMarker.value) {
			editor.value.deleteSelectedMarkers();
			currentSelectedMarker.value = null;
		}
	}

	function initializeEditor() {
		if (container.value) {
			editor.value = reactive(new MarkerArea());

			const imgStyle = document.createElement("style");

			// iOS/Safari does not handle centering the image vertically in the editor nicely so we have to calculate the rendered image and manually set the coordinates

			if (DeviceUtility.isIOS && annotationImage.value) {
				const screenHeight: number = window.innerHeight;
				const screenWidth: number = window.innerWidth;
				const [width, height] = calculateImageDimensions(
					annotationImage.value.naturalWidth,
					annotationImage.value.naturalHeight
				);

				if (orientation.value.includes("portrait")) {
					const top = (screenHeight - height) / 2;

					imgStyle.innerHTML = `
							.canvas-container img { position: absolute; top: ${top}px; }
							.canvas-container svg { margin: 0 !important; position: absolute; top: ${top}px; }
						`;
				} else {
					const left = (screenWidth - width) / 2;

					imgStyle.innerHTML = `
							.canvas-container img { position: absolute; left: ${left}px; }
							.canvas-container svg { margin: 0 !important; position: absolute; left: ${left}px; }
						`;
				}
			} else {
				imgStyle.innerHTML = `
							.canvas-container img { display: flex; justify-self: center; align-self: center; }
							.canvas-container svg { margin: 0 !important; display: flex; justify-self: center; align-self: center; }
						`;
			}

			const canvasStyle = document.createElement("style");
			canvasStyle.innerHTML =
				".canvas-container { place-items: unset !important; overflow: unset !important; } .pinch-zoom-container { height: 100% !important; width: 100% }";
			editor.value.shadowRoot?.appendChild(imgStyle);
			editor.value.shadowRoot?.appendChild(canvasStyle);
			editor.value.registerMarkerType(DotMarker, DotMarkerEditor<DotMarker>);
			editor.value.registerMarkerType(LSTextMarker, LSTextMarkerEditor<LSTextMarker>);
			editor.value.targetImage = annotationImage.value;
			container.value.appendChild(editor.value);

			editor.value.addEventListener("markercreating", (evt: CustomEvent<MarkerEditorEventData>) => {
				const marker = evt.detail.markerEditor.marker;

				if (marker.typeName !== FreehandMarker.typeName) {
					currentEditorTypeName.value = null;
				}
			});

			editor.value.addEventListener("markercreate", (evt: CustomEvent<MarkerEditorEventData>) => {
				const marker = evt.detail.markerEditor.marker;
				repositionMarker(marker, evt.detail.markerEditor);

				if (marker instanceof TextMarker) {
					updateTextMarker(marker, evt.detail.markerEditor as TextMarkerEditor, true);
				} else if (marker instanceof LineMarker) {
				}

				currentState.value = editor.value?.getState();
				console.log(currentState.value);

				if (marker.typeName !== FreehandMarker.typeName) {
					currentEditorTypeName.value = null;
				}

				checkUndo();
			});

			editor.value.addEventListener("markerchange", (evt: CustomEvent<MarkerEditorEventData>) => {
				const marker = evt.detail.markerEditor.marker;

				if (marker instanceof TextMarker) {
					updateTextMarker(marker, evt.detail.markerEditor as TextMarkerEditor);
				} else if (marker instanceof LineMarker) {
				}

				currentState.value = editor.value?.getState();
				checkUndo();
			});

			editor.value.addEventListener("markerdelete", (evt: CustomEvent<MarkerEditorEventData>) => {
				currentState.value = editor.value?.getState();
				checkUndo();
			});

			editor.value.addEventListener("markerdeselect", async (evt: CustomEvent<MarkerEditorEventData>) => {
				const marker = evt.detail.markerEditor.marker;
				currentSelectedMarker.value = undefined;
				currentEditorTypeName.value = null;

				if (marker instanceof LineMarker) {
					//hideLineMarkerBox(marker);
				} else if (marker instanceof TextMarker) {
					updateTextMarker(marker, evt.detail.markerEditor as TextMarkerEditor);
				}

				currentState.value = editor.value?.getState();
				checkUndo();
			});

			editor.value.addEventListener("markerselect", (evt: CustomEvent<MarkerEditorEventData>) => {
				const marker = evt.detail.markerEditor.marker;
				currentSelectedMarker.value = evt.detail.markerEditor;
				if (marker instanceof LineMarker) {
					//showLineMarkerBox(marker);
				}

				currentState.value = editor.value?.getState();
				checkUndo();
			});

			if (wasShowingControls.value) {
				showControls.value = true;
				wasShowingControls.value = false;
			}

			if (currentState.value) {
				editor.value.restoreState(currentState.value, false);
			}

			// Reinitialize the cropper after an orientation change to correctly size and position the image

			if (cropping.value) {
				clearCropper(false);
				nextTick(async () => {
					await initializeCropper();
				});
			}
		}
	}

	function updateColor(newColor: string) {
		annotationColor.value = newColor;
		setMarkerAttributes(true, false);
		Sentry.metrics.increment("Color", 1, {
			tags: {
				conversationId: props.conversationId,
				color: newColor,
			},
		});
	}

	function toggleEdit() {
		showControls.value = !showControls.value;
	}

	function setDimensions() {
		if (editor.value && annotationImage.value) {
			const [width, height] = calculateImageDimensions(
				annotationImage.value.naturalWidth,
				annotationImage.value.naturalHeight
			);

			editor.value.targetHeight = height;
			editor.value.targetWidth = width;
		}
	}

	/**
	 * Updates the current image that will be annotated and re-initializes the editor
	 * @param imageUrl The new Base64 image URL
	 * @param clearState Determines if the state should be cleared or preserved
	 */
	async function updateTargetImage(imageUrl: string | undefined, clearState: boolean = true) {
		clear(clearState);

		if (!clearState) {
			initializeEditor();
			return;
		}

		if (!imageUrl?.startsWith("data:image")) {
			imageUrl = await imageToDataUrl(imageUrl);
		}

		currentImageUrl.value = imageUrl;

		if (annotationImage.value && imageUrl) {
			annotationImage.value.src = imageUrl;
		}
	}

	async function imageToDataUrl(imageUrl: string | undefined): Promise<string> {
		return new Promise((resolve, reject) => {
			if (!imageUrl) {
				reject(new Error("Failed to get image URL"));
				return;
			}

			const canvas = helperCanvas.value;

			if (!canvas) {
				reject(new Error("Failed to get canvas"));
				return;
			}

			const ctx = canvas.getContext("2d");
			if (!ctx) {
				reject(new Error("Failed to get 2D context"));
				return;
			}

			const img = new Image();
			img.crossOrigin = "anonymous"; // required for CORS-safe image to allow toDataURL
			img.onload = () => {
				canvas.width = img.width;
				canvas.height = img.height;
				ctx.drawImage(img, 0, 0);

				try {
					// PNG so we don't lose any data
					const dataUrl = canvas.toDataURL("image/png");
					resolve(dataUrl);
				} catch (err) {
					reject(err);
				}
			};

			img.onerror = (e) => {
				reject(new Error("Failed to load image"));
			};

			img.src = imageUrl;
		});
	}

	/**
	 * Calculates the dimensions of the image as it will be displayed on the screen based on the available screen dimensions
	 * @param imageWidth The image width
	 * @param imageHeight The image height
	 * @returns An array containing the image width (index 0) and height (index 1)
	 */
	function calculateImageDimensions(imageWidth: number, imageHeight: number): Array<number> {
		const maxWidth = window.innerWidth;
		const maxHeight = window.innerHeight;
		const imageAspectRatio = imageWidth / imageHeight;
		const maxAspectRatio = maxWidth / maxHeight;

		let targetWidth, targetHeight;

		if (imageAspectRatio > maxAspectRatio) {
			// Image is relatively wider than container. Make sure that the image width always fills up the width of the screen
			targetWidth = Math.max(maxWidth, Math.min(imageWidth, maxWidth));
			targetHeight = targetWidth / imageAspectRatio;
		} else {
			// Image is relatively taller than container. Make sure that the image width always fills up the height of the screen
			targetHeight = Math.max(maxHeight, Math.min(imageHeight, maxHeight));
			targetWidth = targetHeight * imageAspectRatio;
		}

		return [targetWidth, targetHeight];
	}

	function isMarkerSelected(typeName: string) {
		let selected: boolean = false;
		selected = typeName === currentEditorTypeName.value;
		return selected;
	}

	function checkUndo() {
		if (editor.value) {
			const canUndo = editor.value.editors.length > 0 && editor.value.isUndoPossible;
			undoEnabled.value = canUndo;
			console.debug(
				`Check Undo: MarkerCount=${editor.value.editors.length}, IsUndoPossible=${editor.value.isUndoPossible}`
			);
		}
	}

	function undo() {
		if (editor.value) {
			editor.value?.switchToSelectMode();
			editor.value?.undo();
			currentState.value = editor.value?.getState();
		}

		checkUndo();
		Sentry.metrics.increment("Undo", 1, {
			tags: {
				conversationId: props.conversationId,
			},
		});
	}

	function dot() {
		editor.value?.createMarker(DotMarker);
		currentEditorTypeName.value = DotMarker.typeName;
		setMarkerAttributes();
	}

	function freehand() {
		currentSelectedMarker.value = editor.value?.createMarker(FreehandMarker);
		currentEditorTypeName.value = FreehandMarker.typeName;
		setMarkerAttributes();
	}

	function lineMarker() {
		editor.value?.createMarker(LineMarker);
		currentEditorTypeName.value = LineMarker.typeName;
		setMarkerAttributes();
	}

	function arrowMarker() {
		editor.value?.createMarker(ArrowMarker);
		currentEditorTypeName.value = ArrowMarker.typeName;
		setMarkerAttributes();
		const arrowMarker = editor.value?.currentMarkerEditor as LinearMarkerEditor;
		autoPlaceLinearMarker(arrowMarker);
	}

	/**
	 * Auto adds the linear marker to the annotation canvas
	 * @param markerEditor The marker editor/marker currently being created
	 */
	function autoPlaceLinearMarker(markerEditor: LinearMarkerEditor): void {
		if (!markerEditor) {
			console.warn("LinearMarkerEditor. Not autoplacing");
			return;
		}

		nextTick(() => {
			if (annotationImage.value) {
				const [width, height] = calculateImageDimensions(
					annotationImage.value.naturalWidth,
					annotationImage.value.naturalHeight
				);
				let x = width / 2 - 75;
				let y = height / 2;
				markerEditor.pointerDown({ x: x, y: y } as IPoint);
				markerEditor.marker.x1 = x;
				markerEditor.marker.x2 = x + 150;

				nextTick(() => {
					markerEditor.pointerUp({ x: x + 150, y: y } as IPoint);
				});
			}
		});
	}

	function textMarker() {
		editor.value?.createMarker(LSTextMarker);
		const textMarker = editor.value?.currentMarkerEditor as TextMarkerEditor;
		currentEditorTypeName.value = LSTextMarker.typeName;

		// Automatically add the marker in the center of the image after selecting it from the editor menu

		nextTick(() => {
			if (annotationImage.value) {
				const [width, height] = calculateImageDimensions(
					annotationImage.value.naturalWidth,
					annotationImage.value.naturalHeight
				);
				let x = width / 2 - textMarker.marker.width * 2;
				let y = height / 2 - textMarker.marker.height * 2;
				textMarker.pointerDown({ x: x, y: y } as IPoint);

				// Deselect on next tick in order to render properly

				nextTick(() => {
					textMarker.pointerUp({ x: x, y: y } as IPoint);
					textMarker.deselect();
				});
			}
		});

		setTextMarkerStyle(textMarker);
		setMarkerAttributes();
	}

	function setTextMarkerStyle(textMarker: TextMarkerEditor) {
		textMarker.fontSize = {
			value: 24,
			units: "px",
			step: 1,
		} as FontSize;

		textMarker.marker.fillColor = "#ffffffaa";
		textMarker.marker.container.setAttribute("rx", "8");
		textMarker.marker.textBlock.wordWrap = true;
		textMarker.marker.padding = 8;
		textMarker.marker.adjustVisual();
	}

	function rectangleMarker() {
		editor.value?.createMarker(FrameMarker);
		currentEditorTypeName.value = FrameMarker.typeName;
		setMarkerAttributes();
	}

	function ellipseMarker() {
		editor.value?.createMarker(EllipseMarker);
		currentEditorTypeName.value = EllipseMarker.typeName;
		setMarkerAttributes(false);
	}

	// TODO: MK - Might revisit this to make line selection easier

	// function hideLineMarkerBox(marker: MarkerBase) {
	// 	const lineMarker = marker as LineMarker;
	// 	const line = marker.container.children[0];
	// 	let path: Element | undefined = undefined;
	// 	let rect: Element | undefined = undefined;

	// 	if (line.children[0].nodeName === "rect") {
	// 		rect = line.children[0];
	// 	} else {
	// 		return;
	// 	}

	// 	rect.setAttribute("stroke", "transparent");
	// }

	// function showLineMarkerBox(marker: MarkerBase) {
	// 	const lineMarker = marker as LineMarker;
	// 	const line = marker.container.children[0];
	// 	let path: Element | undefined = undefined;
	// 	let rect: Element | undefined = undefined;

	// 	if (line.children[0].nodeName === "rect") {
	// 		rect = line.children[0];
	// 	} else {
	// 		return;
	// 	}

	// 	rect.setAttribute("stroke", "#fff");
	// }

	/**
	 * Sets the style for the TextMarker when it's created/updated
	 * @param marker The text marker to update
	 * @param markerEditor The editor associated with the text marker
	 * @param creating Determines if the marker is being created, if not, it is being updated
	 */
	function updateTextMarker(marker: TextMarker, markerEditor: TextMarkerEditor, creating = false) {
		lastTextMarker.value = marker;
	}

	function repositionMarker(marker: MarkerBase, markerEditor: MarkerBaseEditor) {
		editor.value?.editors.forEach((editor) => {
			const box = editor.marker.getBBox();
			let x: number = box.x;
			let y: number = box.y;

			if (editor.marker instanceof LinearMarkerBase) {
				x = editor.marker.x1;
				y = editor.marker.y1;
			}

			if (editor != markerEditor) {
				if (marker instanceof TextMarker && x == marker.getBBox().x && y == marker.getBBox().y) {
					marker.left += 16;
					marker.top += 16;
				} else if (marker instanceof LinearMarkerBase) {
					const markerX = marker.x1;
					const markerY = marker.y1;

					if (x == markerX && y == markerY) {
						marker.x1 += 16;
						marker.x2 += 16;
						marker.y1 += 16;
						marker.y2 += 16;
						marker.adjustVisual();
					}
				}
			}
		});
	}

	/**
	 * Initializes the Cropper. If the image has any annotations, a new image will be rendered with the annotations and set on the Cropper instance.
	 * The user will not be able to undo the annotations that have already been rendered.
	 */
	async function initializeCropper(userInitiated: boolean = false): Promise<void> {
		const shadowRoot = editor.value?.shadowRoot;

		if (userInitiated) {
			Sentry.metrics.increment("Crop", 1, {
				tags: {
					conversationId: props.conversationId,
				},
			});
		}

		if (shadowRoot) {
			let src: string | undefined;
			const state = currentState.value;

			if (state && state.markers.length > 0) {
				src = await render(false, state);
			} else {
				src = currentImageUrl.value;
			}

			if (src && annotationImage.value) {
				clearCropper();
				cropping.value = true;
				const copy = document.createElement("img");
				copy.src = src;
				copy.crossOrigin = "anonymous";

				copy.onload = () => {
					const canvas = cropper.value.getCropperCanvas();

					if (canvas) {
						canvas.background = false;
					}

					const shade = cropper.value.container.querySelector("cropper-shade") as HTMLElement;

					if (shade) {
						shade.style.display = "none";
					}

					const selection = cropper.value.getCropperSelection();
					const cropperImage = cropper.value.getCropperImage();

					if (selection && cropperImage) {
						// Lock the aspect ratio

						selection.$clear();
						selection.aspectRatio = cropperImage.$image.width / cropperImage.$image.height;
						selection.initialAspectRatio = selection.aspectRatio;

						selection.$moveTo(
							document.body.clientWidth / 2 - 100,
							document.body.clientHeight / 2 - 100 * selection.aspectRatio
						);

						nextTick(() => {
							selection.onchange = onCropperSelectionChange;
						});

						// Override the zoom function to disable zooming on the image and always return the current one
						cropperImage.$zoom = (scale: number, x: number, y: number): CropperImage => {
							return cropperImage;
						};

						selection.zoomable = false;

						if (mobile.value) {
							selection.zoomable = false;
						}
					}

					if (mobile.value) {
						if (cropperImage) {
							cropperImage.initialCenterSize = "contain";
							cropperImage.rotatable = false;
						}
					}
				};

				cropper.value = new Cropper(copy, {
					container: document.getElementById("crop-container") as HTMLDivElement,
				});
			}
		}
	}

	/**
	 * Clear the Cropper
	 * @param hideCropper Determines if the container should continue to show. This is used during orientation changes so that
	 * the container does not disappear and re-appear, revealing the editor. Makes for a smoother transition.
	 */
	function clearCropper(hideCropper: boolean = true) {
		if (cropper.value) {
			cropper.value = undefined;
			cropContainer.value?.childNodes.forEach((node) => {
				cropContainer.value?.removeChild(node);
			});

			if (hideCropper) {
				cropping.value = false;
			}
		}
	}

	/**
	 * Saves the cropped photo and updates the Base64 imageUrl that will be used to re-initialize the editor
	 */
	async function saveCrop(): Promise<void> {
		if (cropper.value) {
			const cropperCanvas = cropper.value.getCropperSelection();
			const canvas = await cropperCanvas?.$toCanvas();
			const url: string | undefined = canvas?.toDataURL();
			undoEnabled.value = false;

			if (url) {
				await updateTargetImage(url);
				clearCropper();
				Sentry.metrics.increment("SaveCrop", 1, {
					tags: {
						conversationId: props.conversationId,
					},
				});
			}
		}
	}

	/**
	 * Sets the color and stroke of the current marker or currently selected markers
	 * @param setFillColor Parameter that determines if the fill color should be set to the currently selected color, otherwise the color will be transparent
	 */
	function setMarkerAttributes(setFillColor: boolean = true, trackMetric: boolean = true) {
		// Track the metric in Sentry each time a user selects a marker

		if (currentEditorTypeName.value && trackMetric) {
			Sentry.metrics.increment(currentEditorTypeName.value, 1, {
				tags: {
					conversationId: props.conversationId,
				},
			});
		}

		if (editor.value) {
			if (editor.value.currentMarkerEditor) {
				const currentEditor: MarkerBaseEditor<MarkerBase> = editor.value.currentMarkerEditor;
				setMarkerColor(currentEditor, setFillColor);
			} else if (editor.value.selectedMarkerEditors.length > 0) {
				editor.value.selectedMarkerEditors.forEach((editor) => {
					setMarkerColor(editor, setFillColor);
				});
			}
		}
	}

	/**
	 * Sets the color and stroke of the current marker
	 * @param setFillColor Parameter that determines if the fill color should be set to the currently selected color, otherwise the color will be transparent
	 */
	function setMarkerColor(editor: MarkerBaseEditor, setFillColor: boolean = true) {
		if (editor) {
			const color = annotationColor.value == "" ? "#000" : annotationColor.value;

			if (editor instanceof TextMarkerEditor) {
				(editor as TextMarkerEditor).color = color;
				return;
			}

			editor.strokeWidth = 8;
			editor.strokeColor = color;

			if (editor instanceof ShapeMarkerEditor && !(editor instanceof DotMarkerEditor)) {
				setFillColor = false;
			}

			if (setFillColor) {
				editor.fillColor = color;
			} else {
				editor.fillColor = "transparent";
			}
		}
	}

	/**
	 * Event handler that handles the movement of the cropper selection and prevents it from going outside of the image bounds
	 * @param event The event from the Cropper instance
	 */
	function onCropperSelectionChange(event: Event) {
		const evt = event as CustomEvent;
		const selection = evt.detail as Selection;

		const cropperImage = cropper.value?.getCropperImage();
		const cropperCanvas = cropper.value?.getCropperCanvas();

		if (cropperImage && cropperCanvas) {
			const cropperCanvasRect = cropperCanvas.getBoundingClientRect();
			const cropperImageRect = cropperImage.getBoundingClientRect();
			const maxSelection: Selection = {
				x: cropperImageRect.left - cropperCanvasRect.left,
				y: cropperImageRect.top - cropperCanvasRect.top,
				width: cropperImageRect.width,
				height: cropperImageRect.height,
			};

			if (!inBounds(selection, maxSelection)) {
				event.preventDefault();
			}
		}
	}

	/**
	 * Checks to see if the current selection is within the bounds of the current image
	 * @param selection The current selection
	 * @param maxSelection The image bounds
	 */
	function inBounds(selection: Selection, maxSelection: Selection) {
		return (
			selection.x >= maxSelection.x &&
			selection.y >= maxSelection.y &&
			selection.x + selection.width <= maxSelection.x + maxSelection.width &&
			selection.y + selection.height <= maxSelection.y + maxSelection.height
		);
	}

	/**
	 * Clears the editor and all states
	 * @param clearState Determines if the editor state should be cleared. This is used during orientation changes to preserve the state of the editor
	 */
	function clear(clearState: boolean = true) {
		if (editor.value && container.value) {
			if (showControls.value) {
				wasShowingControls.value = true;
			}

			container.value.childNodes.forEach((node) => {
				container.value?.removeChild(node);
			});
			editor.value = null;
			showControls.value = true;
			isSaving.value = false;
			currentEditorTypeName.value = null;
			undoEnabled.value = false;

			if (clearState) {
				currentState.value = null;
			}
		}
	}

	/**
	 * Saves the edited photo to be uploaded to the server or crops the photo (if in cropping mode)
	 */
	async function save(): Promise<void> {
		if (cropping.value) {
			saveCrop();
			return;
		}

		isSaving.value = true;

		try {
			const dataUrl = await render();

			if (dataUrl) {
				const blob = dataURLToBlob(dataUrl);

				if (blob) {
					await emits("imageSaved", blob, true);
					wasShowingControls.value = false;
					Sentry.metrics.increment("SavePhoto", 1, {
						tags: {
							conversationId: props.conversationId,
						},
					});
				}
			}
		} catch (err) {
			Sentry.captureException(err, {
				tags: {
					component: "ImageEditor",
					method: "save",
					conversationId: props.conversationId,
				},
			});
		}
	}

	/**
	 * Renders the current image and annotations in the editor
	 * @param final Determines if this will be the final rendering of the image. If not, PNG will be used to preserve the quality until the final image is rendered
	 * @returns Base64 image data URL
	 */
	async function render(final: boolean = true, state: AnnotationState | null = null): Promise<string | undefined> {
		if (editor.value && currentImageUrl.value) {
			const renderer = new Renderer();
			const mimeType = final ? (DeviceUtility.isSafari ? MediaType.JPG : MediaType.WEBP) : MediaType.PNG;

			renderer.registerMarkerType(DotMarker);
			renderer.registerMarkerType(LSTextMarker);
			renderer.naturalSize = true;
			renderer.imageType = mimeType;
			renderer.imageQuality = imageQuality.value;
			renderer.markersOnly = false;

			const target = document.createElement("img");
			target.src = currentImageUrl.value;
			target.crossOrigin = "anonymous";

			renderer.targetImage = target;
			state = state ?? editor.value.getState();
			adjustState(state);
			const dataUrl = await renderer.rasterize(state);
			return dataUrl;
		}

		return undefined;
	}

	/**
	 * Adjusts the state of the editor to ensure that the freehand markers are always 24px wide on mobile.
	 * Something internally must happening that shrinks the stroke size from 8 to something really small or
	 * the size of the actual image on mobile is affecting the stroke
	 * @param state The state of the editor
	 */
	function adjustState(state: AnnotationState) {
		const markers = state.markers;

		for (const marker of markers) {
			if (marker.typeName === FreehandMarker.typeName && DeviceUtility.isMobile) {
				marker.strokeWidth = 24;
			}
		}
	}

	/**
	 * Resets the Cropper or edtior. If reseting the editor, the user will be prompted with a confirmation dialog before discarding the changes and the photo.
	 * @param reset Determines if the Cropper should be cleared and re-initialized with the original image
	 */
	async function reset(reset = true) {
		if (cropping.value) {
			if (reset) {
				initializeCropper();
				Sentry.metrics.increment("ResetCropper", 1, {
					tags: {
						conversationId: props.conversationId,
					},
				});
			} else {
				clearCropper();
			}
		} else {
			await discard();
		}
	}

	/**
	 * Clears the Cropper or discards the photo/current edits (if any).
	 * The user will be prompted with a confirmation dialog before discarding the changes and the photo.
	 */
	async function discard(): Promise<void> {
		if (cropping.value) {
			clearCropper();
		}

		const markerCount = currentState.value ? currentState.value?.markers.length : 0;

		if (markerCount > 0) {
			const message = "Are you sure you want to discard your changes?";
			const result = await (instance?.proxy.$root as any).$confirm.open(
				"Are you sure?",
				message,
				false,
				"Yes",
				"No",
				true,
				false
			);

			if (!result) {
				return;
			}
		}

		clear();
		emits("discard", true);
		showControls.value = true;
		wasShowingControls.value = false;
	}

	/**
	 * Converts a Base64 data URL to a blob
	 * @param dataUrl The data URL to convert
	 * @returns A blob representation of the data URL
	 */
	function dataURLToBlob(dataUrl: string): Blob | null {
		const [header, base64] = dataUrl.split(",");

		if (!header || header === "") {
			Sentry.captureException("Error parsing data URL", {
				tags: {
					conversationId: props.conversationId,
				},
				extra: {
					dataUrl: dataUrl,
				},
			});

			return null;
		} else {
			const mime = header.match(/:(.*?);/)![1];
			const binary = atob(base64);
			const array = new Uint8Array(binary.length);
			for (let i = 0; i < binary.length; i++) {
				array[i] = binary.charCodeAt(i);
			}
			return new Blob([array], { type: mime });
		}
	}

	watch(
		() => props.targetImage,
		async (newImage, old) => {
			if (newImage) {
				await updateTargetImage(newImage);
			}
		}
	);

	watch(
		() => props.isEditing,
		(isEditing, wasEditing) => {
			if (!isEditing && wasEditing) {
				clear();
				annotationColor.value = "#000";
				showControls.value = true;
				wasShowingControls.value = false;
			}
		}
	);

	watch(
		() => props.targetHeight,
		(newHeight, old) => {
			setDimensions();
		}
	);

	watch(
		() => props.targetWidth,
		(newWidth, old) => {
			setDimensions();
		}
	);
</script>

<style lang="scss" scoped>
	.border-top {
		border-top: solid 2px #000 !important;
	}

	.border-side {
		border-left: solid 2px #000 !important;
	}

	cropper-shade {
		display: none;
	}

	.editor-container {
		z-index: 2000;
	}

	#crop-container {
		position: absolute;
		top: 0;
		left: 0;
		height: 100%;
		width: 100%;
		z-index: 200;
		background: #000;

		:deep(cropper-canvas) {
			height: 100% !important;
			width: 100% !important;
			background-repeat: repeat;
		}
	}

	#marker-container {
		width: 100vw;
		height: 100%;
		background: #000;
	}

	#marker-container :deep(img) {
		object-fit: contain;
		max-width: 100% !important;
		height: auto;
		display: block;
	}

	.close-editing {
		top: 0;
		right: 0;
		z-index: 1001;
	}

	.cancel-crop {
		width: unset !important;
		min-width: unset;
	}

	.rounded-ts-circle {
		border-top-left-radius: 50% !important;
	}

	.rounded-bs-circle {
		border-bottom-left-radius: 50% !important;
	}

	.rounded-te-circle {
		border-top-right-radius: 50% !important;
	}

	.rounded-be-circle {
		border-bottom-right-radius: 50% !important;
	}

	.editor-menu .v-btn,
	.close-editing {
		background-color: #fff !important;
		height: 44px;
		width: 44px;
	}

	.editor-menu .v-btn:not(.close-editing, .close-menu) {
		border-radius: 0;
	}

	.editor-menu .v-btn.selected {
		background-color: #aaa !important;
	}

	.editor-menu .border-primary {
		border-color: #000 !important;
	}

	.editor-menu .v-btn.elevation-3 {
		box-shadow: 0 4px 8px -1px rgba(0, 0, 0, 0.2), 0 2px 4px -1px rgba(0, 0, 0, 0.14) !important;
	}

	.editor-menu .v-btn.shadow-custom {
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3) !important;
	}

	.editor-menu .v-btn :deep(i) {
		color: v-bind(annotationColor) !important;
	}

	.editor-menu .crop.v-btn,
	.editor-menu .crop :deep(i),
	.editor-menu .trash.v-btn,
	.editor-menu .trash :deep(i),
	.editor-menu .undo.v-btn,
	.editor-menu .undo :deep(i) {
		color: #000 !important;
	}

	.editor-menu .close-editing.v-btn,
	.editor-menu .close-editing.v-btn :deep(i),
	.editor-menu .close-menu.v-btn,
	.editor-menu .close-menu.v-btn :deep(i) {
		color: #000 !important;
	}

	.editor-menu .v-btn {
		color: v-bind(annotationColor) !important;
		min-width: unset;
		border: none;
	}

	.bottom-container {
		bottom: 0;
		width: 100%;
		z-index: 250;
	}

	.v-btn.disabled {
		:deep(.v-icon) {
			color: #aaa;
		}
	}

	.v-btn--disabled {
		:deep(.v-btn__overlay) {
			opacity: 1;
			background-color: #fff;
		}
	}

	.image-progress {
		z-index: 10000;
		position: absolute;
		top: calc(50% - 36px);
		left: calc(50% - 36px);
	}
</style>
